import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "../axiosInstance";
import HighPerformanceAd from "../components/HighPerformanceAd";
import { Sidebar } from "../components/Sidebar";
import { Header } from "../components/Header";
import { Footer } from "../components/ Footer";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

function Chevron({ open }) {
  return (
    <svg
      className={`w-5 h-5 transform transition-transform duration-300 ${
        open ? "rotate-90" : "rotate-0"
      } text-cyan-400`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function FaqItem({ question, children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-700">
      <button
        className="w-full flex justify-between items-center py-4 text-left text-lg font-semibold text-cyan-400 hover:text-cyan-300 transition focus:outline-none"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {question}
        <Chevron open={open} />
      </button>
      {open && <div className="pb-6 text-gray-300 leading-relaxed">{children}</div>}
    </div>
  );
}

export default function HowItWorks({ onLogout }) {
  const [options, setOptions] = useState(0);
  const [coinz, setCoinz] = useState(0);
  const [weeklyProfit, setWeeklyProfit] = useState(0);
  const [selfWeeklyProfit, setSelfWeeklyProfit] = useState(0);

  // 📡 Récupération des données utilisateur via l'API
  const getUserData = () => {
    axios
      .get(`/api/user/me`, { withCredentials: true })
      .then((response) => {
        const {
          coinz,
          options,
          systemCoinz,
          weeklyProfit,
          // autres champs si besoin
        } = response.data;

        const valeurCalculee =
          options > 0 && weeklyProfit > 0 ? (systemCoinz / weeklyProfit) * options : 0;

        setSelfWeeklyProfit(valeurCalculee);
        setCoinz(coinz);
        setOptions(options);
        setWeeklyProfit(weeklyProfit);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });
  };

  useEffect(() => {
    document.title = "How CoinzUp Works | CoinzUp";
    getUserData();
  }, []);

  return (
<div className="min-h-screen flex flex-col bg-gray-900 text-gray-100 font-sans">
    {/* Contenu principal avec sidebar */}
    <div className="flex flex-1 min-h-[calc(100vh-64px)]">
      {/* Sidebar pleine hauteur */}
      <Sidebar onLogout={onLogout} />

      {/* Contenu à droite */}
      <main className="flex-1 px-8 py-10 space-y-12 overflow-auto min-w-0 bg-gray-900">
        <Header 
          title="FAQ - How it works "  
          balance={coinz}        
          optionsOwned={options}        
          estimatedWeeklyGain={selfWeeklyProfit} 
        />

        <HighPerformanceAd
          keyId="fd5da3543c594f07b73630f461f10613"
          width={728}
          height={90}
        />

          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-md divide-y divide-gray-700">
          <FaqItem question="1. AutoSurf">
            <>
              AutoSurf is our passive earning system. When active, it cycles through community ads and websites — and rewards you up to <strong className="text-green-400">12 Coinz per minute</strong> depending on your user level.

              <p className="mt-2 text-gray-300 text-sm">
                As your level increases, your earnings per minute grow — from 4 Coinz at Level 1, up to 12 Coinz at higher levels. See{" "}
                <Link to="/levelup" className="underline text-cyan-400 hover:text-cyan-300">
                  Level Up
                </Link>{" "}
                for the full progression.
              </p>

              <p className="mt-2 font-semibold text-gray-100">
                💡 Pro Tip: Run AutoSurf in a background tab or during downtime to maximize passive income.
              </p>
            </>
          </FaqItem>

          <FaqItem question="2. Hourly Draw">
            <>
              Every hour, you can participate in a lucky draw to win up to <strong className="text-yellow-300">300 Coinz</strong>. Your chance of winning — and the prize amount — scale with your level.

              <p className="mt-2 text-gray-300 text-sm">
                Participating is free, and you can try once per hour. Rewards are added instantly to your balance.
              </p>
            </>
          </FaqItem>

          <FaqItem question="3. Marketplace">
            <>
              The Marketplace allows you to trade <strong className="text-blue-300">options</strong> — unique assets that generate passive weekly profits.

              <p className="mt-2 text-gray-300 text-sm">
                You can buy low, sell high, or hold long-term to collect rewards. Every option you own contributes to your weekly Coinz income.
              </p>

              <p className="mt-2 text-gray-300 text-sm">
                📘 Learn how options work in this{" "}
                <Link to="/blog/owingoptions" className="underline text-cyan-400 hover:text-cyan-300">
                  detailed blog post
                </Link>
                .
              </p>
            </>
          </FaqItem>

          <FaqItem question="4. Bonuses">
            <>
              Daily activities unlock random bonuses — like XP boosts, Coinz drops, or extra views. They're designed to reward consistency and engagement.

              <p className="mt-2 text-gray-300 text-sm">
                You don’t need to claim them — just use the platform actively and enjoy surprises along the way!
              </p>
            </>
          </FaqItem>

          <FaqItem question="5. Referral Program">
            <>
              Share your referral link and earn lifetime rewards from your invitees.

              <ul className="list-disc list-inside mt-2 text-sm space-y-1 text-gray-300">
                <li>+10 Free Options per verified referral</li>
                <li>Earn <strong>5%</strong> of their AutoSurf, ad, and draw earnings</li>
                <li>Track all referrals and commissions in your dashboard</li>
              </ul>
            </>
          </FaqItem>

            <FaqItem question="6. Buy Ads">
                <>
                  Buying ads lets you drive real traffic to your website. For each view purchased, your site is shown in the AutoSurf and PTP networks. This brings exposure, visibility, and engagement from active users.

                  <p className="mt-3 text-gray-300 text-sm">
                    Each ad purchase also grants you the exact same amount in XP — helping you level up faster and unlock better Coinz rates in AutoSurf and Draws.
                  </p>

                  <p className="mt-3 text-gray-300 text-sm">
                    Ads require a <strong className="text-blue-400">minimum of 2000 views</strong>. Each view costs <strong>1 Coinz</strong>, and you can purchase more in increments of 100.
                  </p>

                  <p className="mt-3 text-gray-300 text-sm">
                    🔒 All ads are reviewed before approval to ensure safety and quality across the network.
                  </p>
                </>
              </FaqItem>


              <FaqItem question="7. Withdraw Coinz">
                <>
                  You can withdraw your Coinz once you meet the minimum required balance for your preferred cryptocurrency. Multiple options are available to suit your needs:

                  <ul className="list-disc list-inside mt-3 text-sm text-gray-300 space-y-1">
                    <li><strong className="text-white">BTC</strong>: 1,000,000 Coinz minimum</li>
                    <li><strong className="text-white">ETH</strong>: 200,000 Coinz minimum</li>
                    <li><strong className="text-white">SOL</strong>: 100,000 Coinz minimum</li>
                    <li><strong className="text-white">WAVES</strong>: 100,000 Coinz minimum</li>
                  </ul>

                  <p className="mt-4 text-gray-300 text-sm">
                    <strong className="text-white">Fees:</strong> BTC (2%), ETH (1%), SOL & WAVES (0.5%). Fees are automatically deducted from the amount you withdraw.
                  </p>

                  <p className="mt-3 text-gray-300 text-sm">
                    <strong className="text-white">Processing Time:</strong> Withdrawals are reviewed and processed manually within <span className="text-green-400 font-semibold">48 hours</span>.
                  </p>

                  <p className="mt-3 text-yellow-300 text-sm">
                    💡 Double-check your wallet address before confirming. Transactions are irreversible once sent.
                  </p>
                </>
              </FaqItem>

            <FaqItem question="8. Pay To Promote (PTP)">
              <>
                Share your personal PTP link and earn Coinz every time someone clicks it. This is one of the easiest ways to grow your balance passively.

                <ul className="list-disc list-inside text-sm space-y-1 text-gray-300 mt-3">
                  <li><strong>+100 Coinz</strong> per unique IP visitor</li>
                  <li><strong>+1 Coinz</strong> per repeat IP (up to 10x/day)</li>
                  <li><strong>⚠️ Views must last at least 15 seconds to qualify</strong></li>
                  <li>You also earn <strong>50%</strong> of your referrals' PTP rewards</li>
                </ul>

                <p className="mt-2 text-gray-300 text-sm">
                  🔗 Post your PTP link on social media, websites, communities — anywhere that drives traffic!
                </p>
              </>
            </FaqItem>

            <FaqItem question="Final Tips">
            <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
              <li>Use AutoSurf as often as possible — it's your main passive income</li>
              <li>Check in hourly for bonus draws</li>
              <li>Hold and sell options strategically</li>
              <li>Level up to increase XP and earnings multipliers</li>
              <li>Refer users to grow long-term income</li>
              <li>Monitor stats in your dashboard to optimize gains</li>
            </ul>
          </FaqItem>
          </div>

          <div className="mt-12 bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-md text-gray-300">
            <h2 className="text-xl font-semibold mb-6 text-cyan-400 tracking-wide">
              Estimated Earnings Breakdown
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={[
                  { name: "AutoSurf", value: 1960 },
                  { name: "Draws", value: 750 },
                  { name: "Options", value: 450 },                  
                  
                  { name: "Bonuses", value: 100 },
                  { name: "Referrals", value: 45 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
                <XAxis dataKey="name" stroke="#cbd5e0" />
                <YAxis stroke="#cbd5e0" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1a202c", borderColor: "#4a5568", color: "#fff" }}
                />
                <Bar dataKey="value" fill="#22d3ee" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <HighPerformanceAd
            keyId="96e99453a24be0353f2fe26dc0b9b728"
            width={468}
            height={60}
            className="mt-10 mx-auto"
          />
        </main>
      </div>

      <Footer />
    </div>
  );
}
